/** Shared confirmation dialog replacing window.confirm (UX review C2).
 *
 * Provides a styled, accessible alternative to native confirms with:
 * - affected-resource summary (`details`)
 * - severity-aware confirm button
 * - optional type-to-confirm for irreversible operations
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { Warning as WarningIcon } from "@mui/icons-material";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Main question, e.g. "Remove server fs-01?" */
  message: React.ReactNode;
  /** Optional affected-resource summary (rows, scope, consequences). */
  details?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  severity?: "error" | "warning" | "primary";
  /** When set, the user must type this phrase to enable the confirm button. */
  requirePhrase?: string;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/** Confirmation dialog component. */
export default function ConfirmDialog({
  open,
  title,
  message,
  details,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  severity = "primary",
  requirePhrase,
  loading = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const [phrase, setPhrase] = useState("");
  const [lastOpen, setLastOpen] = useState(false);

  // Reset the type-to-confirm input each time the dialog opens
  useEffect(() => {
    if (open && !lastOpen) setPhrase("");
    setLastOpen(open);
  }, [open, lastOpen]);

  const phraseSatisfied = useMemo(
    () => !requirePhrase || phrase.trim().toUpperCase() === requirePhrase.toUpperCase(),
    [requirePhrase, phrase]
  );

  const severityColor =
    severity === "error" ? "error" : severity === "warning" ? "warning" : "primary";

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      aria-labelledby="confirm-dialog-title"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="confirm-dialog-title" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {severity !== "primary" && <WarningIcon color={severityColor} />}
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: details ? 1.5 : 0 }}>
          {message}
        </Typography>
        {details && (
          <Typography variant="body2" color="text.secondary" component="div">
            {details}
          </Typography>
        )}
        {requirePhrase && (
          <TextField
            autoFocus
            margin="dense"
            label={`Type "${requirePhrase}" to confirm`}
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            disabled={loading}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={severityColor as "error" | "warning" | "primary"}
          disabled={loading || !phraseSatisfied}
        >
          {loading ? "Working…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
